import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { Amplify } from 'aws-amplify'
import { signIn, signOut, getCurrentUser, fetchAuthSession, confirmSignIn, SignInInput } from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { defaultStorage } from 'aws-amplify/utils'
import { extractRoleFromToken, extractTenantIdFromToken, extractUserTypeFromToken, isPlatformAdmin } from '../utils/jwt'

// Configure Amplify (these values should come from environment variables)
const awsConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || 'us-east-1_XXXXXXXXX',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || 'XXXXXXXXXXXXXXXXXXXXXXXXXX',
      region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
      loginWith: {
        username: true
      }
    }
  }
}

// Extract tenantId from the current subdomain.
// e.g. "rockclimbers.elcapit.com" → "rockclimbers"
// Falls back to VITE_FALLBACK_TENANT_ID (used on apex domain or local dev).
function getTenantIdFromSubdomain(): string {
  const host = window.location.hostname
  const parts = host.split('.')
  // subdomain present if we have ≥3 parts (e.g. gym.elcapit.com)
  if (parts.length >= 3) {
    return parts[0]
  }
  return import.meta.env.VITE_FALLBACK_TENANT_ID || 'legacy'
}

Amplify.configure(awsConfig)

// Use localStorage for all environments. Each subdomain (gymA.elcapit.com,
// gymB.elcapit.com) has its own localStorage origin, so sessions are already
// isolated without needing cookies.
cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage)

// Diagnostic logging to verify environment variable loading
console.log('Loaded Cognito Client ID:', import.meta.env.VITE_COGNITO_CLIENT_ID)

interface User {
  username: string
  email?: string
  attributes?: Record<string, any>
  role?: string | null
  tenantId?: string | null
  isPlatformAdmin: boolean
  userType?: string | null
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<string | null>
  completeNewPassword: (newPassword: string) => Promise<string | null>
  logout: () => Promise<void>
  isAuthenticated: boolean
  getToken: () => Promise<string | null>
  isPlatformAdmin: boolean
  role: string | null
  tenantId: string | null
  userType: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState<string | null>(null)
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [userType, setUserType] = useState<string | null>(null)
  const [pendingUserName, setPendingUserName] = useState<string | null>(null) // Store name for password change
  const hasCheckedUser = useRef(false) // Track if initial auth check has been performed

  // Debug logging for user state changes
  useEffect(() => {
    console.log('[AuthContext] User state changed:', user ? `Authenticated as ${user.username}` : 'Not authenticated')
  }, [user])

  useEffect(() => {
    console.log('[AuthContext] AuthProvider mounted')
    
    // Check for persisted auth errors from previous session
    const lastError = sessionStorage.getItem('lastAuthError')
    if (lastError) {
      console.warn('[AuthContext] Previous auth error found:', JSON.parse(lastError))
      console.warn('[AuthContext] To clear this error, run: sessionStorage.removeItem("lastAuthError")')
    }
    
    // Only call checkUser once on initial mount
    if (!hasCheckedUser.current) {
      hasCheckedUser.current = true
      // Call checkUser with isInitialLoad=true only on first mount
      checkUser(true)
    }
    
    return () => {
      console.log('[AuthContext] AuthProvider unmounting')
    }
  }, [])

  async function checkUser(isInitialLoad: boolean = false) {
    try {
      // In dev mode without Cognito, skip the check - user must call login() first
      if (import.meta.env.DEV && !import.meta.env.VITE_COGNITO_USER_POOL_ID) {
        // Don't auto-authenticate - let login() handle mock authentication
        setLoading(false)
        return
      }
      
      console.log('[AuthContext] checkUser: Starting authentication check')
      const currentUser = await getCurrentUser()
      console.log('[AuthContext] checkUser: Got current user:', currentUser.username)
      
      // Get the JWT token to extract role information
      // Handle missing custom attributes gracefully
      const token = await getTokenInternal()
      console.log('[AuthContext] checkUser: Got token:', token ? 'yes' : 'no')
      
      let userRole: string | null = null
      let userTenantId: string | null = null
      let userIsAdmin = false
      let userUserType: string | null = null

      if (token) {
        try {
          userRole = extractRoleFromToken(token)
          userTenantId = extractTenantIdFromToken(token)
          userIsAdmin = isPlatformAdmin(token)
          userUserType = extractUserTypeFromToken(token)
          console.log('[AuthContext] checkUser: Extracted role:', userRole, 'tenantId:', userTenantId, 'userType:', userUserType)
        } catch (tokenError) {
          // If custom attributes are missing, set to null (don't fail authentication)
          console.log('[AuthContext] checkUser: Custom attributes missing or invalid, setting to null:', tokenError)
          userRole = null
          userTenantId = null
          userIsAdmin = false
          userUserType = null
        }
      }

      setUser({
        username: currentUser.username,
        attributes: currentUser.signInDetails?.loginId ? {
          // Strip tenantId:: prefix from composite usernames to get the plain email
          email: currentUser.signInDetails.loginId.includes('::')
            ? currentUser.signInDetails.loginId.split('::')[1]
            : currentUser.signInDetails.loginId
        } : {},
        role: userRole,
        tenantId: userTenantId,
        isPlatformAdmin: userIsAdmin,
        userType: userUserType
      })
      setRole(userRole)
      setTenantId(userTenantId)
      setIsAdmin(userIsAdmin)
      setUserType(userUserType)
      console.log('[AuthContext] checkUser: User state updated successfully')
    } catch (error: any) {
      // On initial load, authentication errors are expected (user not logged in yet)
      // Suppress these to avoid console noise
      const isExpectedAuthError = error?.name === 'UserUnAuthenticatedException' || 
                                   error?.name === 'NotAuthorizedException'
      
      if (isExpectedAuthError && isInitialLoad) {
        // Silently handle expected initial load errors - no logging needed
      } else if (isExpectedAuthError) {
        // Auth error on subsequent checks (e.g., session expired)
        console.log('[AuthContext] Session expired or user logged out')
      } else {
        // Unexpected error - log for debugging
        const errorLog = {
          timestamp: new Date().toISOString(),
          name: error?.name,
          message: error?.message,
          code: error?.code,
          stack: error?.stack
        }
        console.error('[AuthContext] checkUser ERROR:', errorLog)
        sessionStorage.setItem('lastAuthError', JSON.stringify(errorLog))
        console.error('[AuthContext] ERROR PERSISTED TO sessionStorage - check sessionStorage.getItem("lastAuthError")')
      }
      
      // Clear user state on authentication errors
      if (isExpectedAuthError) {
        setUser(null)
        setRole(null)
        setTenantId(null)
        setIsAdmin(false)
      }
    } finally {
      setLoading(false)
    }
  }

  async function login(email: string, password: string): Promise<string | null> {
    try {
      // Mock authentication for local development
      if (import.meta.env.DEV && !import.meta.env.VITE_COGNITO_USER_POOL_ID) {
        // Bypass Cognito for local testing
        // For testing, you can set a mock role via environment variable
        const mockRole = import.meta.env.VITE_MOCK_USER_ROLE || null
        const mockTenantId = import.meta.env.VITE_MOCK_TENANT_ID || null
        const mockIsAdmin = mockRole === 'platform_admin'
        
        setUser({
          username: email,
          attributes: { email },
          role: mockRole,
          tenantId: mockTenantId,
          isPlatformAdmin: mockIsAdmin
        })
        setRole(mockRole)
        setTenantId(mockTenantId)
        setIsAdmin(mockIsAdmin)
        setLoading(false)
        return mockTenantId
      }

      // Sign out any existing user first to avoid UserAlreadyAuthenticatedException
      try {
        await signOut()
        console.log('[AuthContext] Signed out existing user before new login')
      } catch (signOutError) {
        // Ignore errors if no user was signed in
        console.log('[AuthContext] No existing user to sign out')
      }

      const tenantId = getTenantIdFromSubdomain()
      const signInInput: SignInInput = {
        username: `${tenantId}::${email.toLowerCase().trim()}`,
        password: password
      }
      let result
      try {
        result = await signIn(signInInput)
      } catch (signInError: any) {
        // If a stale session survived signOut, force-clear and retry once
        if (signInError.name === 'UserAlreadyAuthenticatedException') {
          await signOut()
          result = await signIn(signInInput)
        } else {
          throw signInError
        }
      }

      // Store email as pending name for password change flow
      setPendingUserName(email)
      
      // Check if new password is required
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        throw new Error('NEW_PASSWORD_REQUIRED')
      }
      
      await checkUser(false)  // Not initial load - this is after login

      // Extract tenantId directly from the session token so the caller can use it
      // immediately (React state from checkUser won't be visible until next render)
      try {
        const session = await fetchAuthSession()
        const idToken = session.tokens?.idToken?.toString()
        if (idToken) return extractTenantIdFromToken(idToken)
      } catch {
        // non-fatal — caller falls back to navigate('/dashboard')
      }
      return null
    } catch (error: any) {
      console.error('Login error:', error)
      console.error('Authentication failed with Client ID:', import.meta.env.VITE_COGNITO_CLIENT_ID)
      throw error
    }
  }

  async function completeNewPassword(newPassword: string): Promise<string | null> {
    try {
      // Use the stored name from login attempt
      const userName = pendingUserName || 'User'
      
      // Amplify v6 confirmSignIn with user attributes
      await confirmSignIn({ 
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            name: userName
          }
        }
      })
      
      // Clear pending name after successful password change
      setPendingUserName(null)
      
      await checkUser(false)  // Not initial load - this is after password change

      // Return tenantId directly from session token so caller doesn't rely on stale state
      try {
        const session = await fetchAuthSession()
        const idToken = session.tokens?.idToken?.toString()
        if (idToken) return extractTenantIdFromToken(idToken)
      } catch {
        // non-fatal
      }
      return null
    } catch (error: any) {
      console.error('Complete new password error:', error)
      throw new Error(error.message || 'Failed to set new password')
    }
  }

  async function logout() {
    try {
      await signOut()
      setUser(null)
      setRole(null)
      setTenantId(null)
      setIsAdmin(false)
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  async function getTokenInternal(): Promise<string | null> {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString() || null
    } catch (error) {
      console.error('Get token error:', error)
      return null
    }
  }

  async function getToken(): Promise<string | null> {
    return getTokenInternal()
  }

  const value = {
    user,
    loading,
    login,
    completeNewPassword,
    logout,
    isAuthenticated: !!user,
    getToken,
    isPlatformAdmin: isAdmin,
    role,
    tenantId,
    userType
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
